import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthService } from '../services/authService';
import { ApiService } from '../services/apiService';
// @mcc-dev-preview:import
import { parseDevState } from '../dev/devPreview';
import './EmailValidation.css';

// @mcc-dev-preview:start dev-preview-types
type EmailValidationAuthDevState = 'form' | 'loading' | 'success' | 'error';

const EMAIL_VALIDATION_AUTH_DEV_STATES: EmailValidationAuthDevState[] = [
  'form',
  'loading',
  'success',
  'error',
];
// @mcc-dev-preview:end dev-preview-types

interface ValidationState {
  status: 'idle' | 'loading' | 'success' | 'error';
  message: string;
  user?: any;
}

const EmailValidationAuth: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const devState = parseDevState(searchParams, EMAIL_VALIDATION_AUTH_DEV_STATES);
  const [validationState, setValidationState] = useState<ValidationState>({
    status: 'idle',
    message: ''
  });

  const token = searchParams.get('token');
  const cardId = searchParams.get('cardId');
  const returnTo = searchParams.get('returnTo');

  const validateToken = useCallback(async (validationToken: string, cardId: string) => {
    try {
      setValidationState({
        status: 'loading',
        message: 'Validating your email...'
      });

      // Use the authentication-only endpoint (no card linking logic)
      const result = await ApiService.validateEmailTokenForAuth(validationToken, cardId);

      if (result.success) {
        setValidationState({
          status: 'success',
          message: 'Email validated successfully!',
          user: result.data
        });

        // Store the JWT token for authentication
        if (result.data.jwtToken) {
          localStorage.setItem('authToken', result.data.jwtToken);
          localStorage.setItem('user', JSON.stringify(result.data));
        }
        if (result.data && result.data.jwtToken) {
          AuthService.setToken(result.data.jwtToken, {
            userEmail: result.data.userEmail,
            ...result.data
          });
        }
        
        // Redirect to the original page they were trying to access
        setTimeout(() => {
          if (returnTo) {
            navigate(returnTo);
          } else {
            // Default redirect to balance display if no returnTo
            navigate(`/balance-display?cardId=${cardId}`);
          }
        }, 2000);
      } else {
        setValidationState({
          status: 'error',
          message: result.message || 'Validation failed'
        });
      }
    } catch (error) {
      console.error('Error validating token:', error);
      setValidationState({
        status: 'error',
        message: 'Network error. Please try again.'
      });
    }
  }, [navigate, returnTo]);

  // @mcc-dev-preview:start dev-preview-seed
  useEffect(() => {
    if (!devState) return;

    switch (devState) {
      case 'form':
        setValidationState({ status: 'idle', message: '' });
        break;
      case 'loading':
        setValidationState({ status: 'loading', message: 'Validating your email...' });
        break;
      case 'success':
        setValidationState({
          status: 'success',
          message: 'Email validated successfully!',
        });
        break;
      case 'error':
        setValidationState({
          status: 'error',
          message: searchParams.get('errorMessage') ?? 'Validation failed',
        });
        break;
      default:
        break;
    }
  }, [devState, searchParams]);
  // @mcc-dev-preview:end dev-preview-seed

  useEffect(() => {
    if (devState) return;
    if (token && cardId) {
      validateToken(token, cardId);
    } else {
      setValidationState({
        status: 'error',
        message: 'Missing validation token or card ID'
      });
    }
  }, [token, cardId, validateToken, devState]);

  const handleResendEmail = () => {
    // TODO: Implement resend email functionality
  };

  const renderContent = () => {
    switch (validationState.status) {
      case 'loading':
        return (
          <div className="validation-container">
            <div className="loading-spinner"></div>
            <h2>Validating your email...</h2>
            <p>{validationState.message}</p>
          </div>
        );

      case 'success':
        return (
          <div className="validation-container success">
            <div className="success-icon">✓</div>
            <h2>Email Validated!</h2>
            <p>{validationState.message}</p>
            <p>Redirecting to your requested page...</p>
          </div>
        );

      case 'error':
        return (
          <div className="validation-container error">
            <div className="error-icon">✗</div>
            <h2>Validation Failed</h2>
            <p>{validationState.message}</p>
            <div className="error-actions">
              <button onClick={handleResendEmail} className="resend-button">
                Resend Validation Email
              </button>
              <button onClick={() => navigate('/welcome')} className="home-button">
                Return to Home
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="validation-container">
            <h2>Email Validation</h2>
            <p>Please wait while we process your validation...</p>
          </div>
        );
    }
  };

  return (
    <div className="email-validation-page email-validation-auth-page">
      <div className="validation-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default EmailValidationAuth;








